import { FormControl, FormHelperText, TextField, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect } from "react";
import { ManagerService } from "../../../../services/manager-service";
import { useToast } from "../../../../context/toast-context";
import { ManagerResponseDto } from "../../../../services/dto/response/manager-response-dto";
import { ButtonForm } from "../../button-form";
import { BranchService } from "../../../../services/branch-service";
import { Branch } from "../../../../types";
import { useAuth } from "../../../../context/auth-context";

interface AddManagerFormDOProps {
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onAddManager?: (newManager: ManagerResponseDto) => void;
  fetchManagers?: () => Promise<void>;
}

/**
 * Formulario de creación de manager para República Dominicana
 * Los managers de DO ingresan Cédula en lugar de CUIL
 * NO ingresan provincia ni ciudad
 */
export function AddManagerFormDO({
  setIsModalOpen,
  onAddManager,
  fetchManagers,
}: AddManagerFormDOProps) {
  const { t } = useTranslation();
  const { country } = useAuth();
  const [loading, setLoading] = useState(false);
  const managerService = useMemo(() => new ManagerService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const { showToast } = useToast();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    cedula: "",
    address: "",
    phone: "",
    branchesIds: "",
  });

  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    IdentityType: "CEDULA",
    IdentityNumber: "", // Usamos el mismo campo pero para cédula
    Address: "",
    Phone: "",
    DepartmentId: 0, // No requerido para DO
    CityId: 0, // No requerido para DO
    BranchesIds: [] as number[],
  });

  useEffect(() => {
    const loadBranches = async () => {
      try {
        await branchService.getBranches(
          { limit: 100, offset: 0 },
          (data) => {
            setBranches(data.branches);
          },
          (errorMessage) => {
            showToast(errorMessage, "error");
          }
        );
      } catch (error) {
        showToast(t("common.errors.generic"), "error");
      }
    };

    loadBranches();
  }, [branchService, showToast, t]);

  const validate = () => {
    let isValid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      cedula: "",
      address: "",
      phone: "",
      branchesIds: "",
    };

    if (!formData.FirstName) {
      newErrors.firstName = t("managers.create.errors.name");
      isValid = false;
    }

    if (!formData.LastName) {
      newErrors.lastName = t("managers.create.errors.lastName");
      isValid = false;
    }

    if (!formData.Email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      newErrors.email = t("managers.create.errors.email");
      isValid = false;
    }

    // Validación de cédula dominicana: debe tener entre 8 y 11 dígitos
    if (!formData.IdentityNumber) {
      newErrors.cedula = t("managers.create.errors.cedula");
      isValid = false;
    } else if (formData.IdentityNumber.length < 8 || formData.IdentityNumber.length > 11) {
      newErrors.cedula = t("managers.create.errors.cedula-length");
      isValid = false;
    } else if (!/^\d+$/.test(formData.IdentityNumber)) {
      newErrors.cedula = t("managers.create.errors.cedula-format");
      isValid = false;
    }

    if (formData.BranchesIds.length === 0) {
      newErrors.branchesIds = t("managers.create.errors.branchesIds");
      isValid = false;
    }

    if (!formData.Address) {
      newErrors.address = t("managers.create.errors.address");
      isValid = false;
    } else if (formData.Address.length < 5) {
      newErrors.address = t("managers.create.errors.address-min");
      isValid = false;
    } else if (formData.Address.length > 50) {
      newErrors.address = t("managers.create.errors.address-max");
      isValid = false;
    }

    if (!formData.Phone) {
      newErrors.phone = t("managers.create.errors.phone-required");
      isValid = false;
    } else if (formData.Phone.length < 7 || formData.Phone.length > 20) {
      newErrors.phone = t("managers.create.errors.phone-size");
      isValid = false;
    }


    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      await managerService.createManager(
        {
          ...formData,
          DepartmentId: "0", // No requerido para DO
          CityId: "0", // No requerido para DO
          CountryCode: country ?? "DO",
        },
        async (data) => {
          setIsModalOpen && setIsModalOpen(false);
          showToast(t("managers.create.success"), "success");
          if (fetchManagers) {
            await fetchManagers();
          }
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
    const selectedOptions = event.target.value as number[];
    setSelectedBranches(selectedOptions);
    setFormData((prev) => ({ ...prev, BranchesIds: selectedOptions }));
  };

  const textFieldStyles = {
    "& .MuiInput-root": {
      color: "#000",
      fontFamily: "Montserrat",
      "&:before": {
        borderBottom: "1px solid #838383",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#898989",
      },
    },
    "& label.Mui-focused": {
      color: "#7e7e7e",
    },
    "& .MuiInputLabel-standard": {
      color: "#000",
      fontWeight: "400",
      fontFamily: "Montserrat",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#838383",
    },
  };

  return (
    <form onSubmit={handleSubmit} className="">
      <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
        <h1>{t("managers.create-title")}</h1>
      </div>
      <div className="grid gap-4 p-6">
        <TextField
          type="text"
          name="FirstName"
          error={!!errors.firstName}
          helperText={errors.firstName}
          label={t("managers.create.name")}
          value={formData.FirstName}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="LastName"
          error={!!errors.lastName}
          helperText={errors.lastName}
          label={t("managers.create.lastName")}
          value={formData.LastName}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="email"
          name="Email"
          error={!!errors.email}
          helperText={errors.email}
          label={t("managers.create.email")}
          value={formData.Email}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        {/* En República Dominicana usan Cédula en lugar de CUIL */}
        <TextField
          type="text"
          name="IdentityNumber"
          error={!!errors.cedula}
          helperText={errors.cedula}
          label={t("managers.create.cedula", "Cédula")}
          value={formData.IdentityNumber}
          onChange={handleChange}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            let value = input.value;
            value = value.replace(/[^\d]/g, "");
            // Permitir hasta 11 dígitos para República Dominicana
            if (value.length > 11) {
              value = value.substring(0, 11);
            }
            input.value = value;
            setFormData(prev => ({ ...prev, IdentityNumber: value }));
          }}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="Address"
          error={!!errors.address}
          helperText={errors.address}
          label={t("managers.create.address")}
          value={formData.Address}
          onChange={handleChange}
          variant="standard"
          sx={textFieldStyles}
        />

        <TextField
          type="text"
          name="Phone"
          error={!!errors.phone}
          helperText={errors.phone}
          label={t("managers.create.phone")}
          value={formData.Phone}
          onChange={handleChange}
          onInput={(e) => {
            const input = e.target as HTMLInputElement;
            let value = input.value;
            value = value.replace(/[^\d]/g, "");
            if (value.length > 10) {
              value = value.substring(0, 10);
            }
            input.value = value;
            setFormData(prev => ({ ...prev, Phone: value }));
          }}
          variant="standard"
          sx={textFieldStyles}
        />

        {/* República Dominicana NO requiere provincia ni ciudad */}

        <FormControl error={!!errors.branchesIds} variant="standard" fullWidth>
          <Select
            sx={{
              paddingX: 2,
              paddingY: 1,
              margin: 0,
              fontStyle: "normal",
              fontFamily: "Montserrat",
              fontSize: "1rem",
              height: "100%",
              width: "100%",
              borderRadius: "9999px",
              border: "1px solid",
              borderColor: "#838383",
              backgroundColor: "transparent",
              outline: "none",
              "&:focus": {
                outlineColor: "primary.main",
              },
              "& .MuiSelect-select": {
                padding: 0,
                display: "block",
                minWidth: 0,
              },
              "&.MuiInputBase-root.MuiOutlinedInput-root": {
                padding: "4px 8px",
              },
              "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                {
                  WebkitTextFillColor: "#838383",
                  opacity: "70%",
                },
              "& .MuiSvgIcon-root.MuiSelect-icon": {
                color: "white",
              },
              color: "#000",
              fontWeight: "400",
              "&:before": {
                borderBottom: "none"
              },
              "&:after": {
                borderBottom: "none"
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: "none"
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "white",
                  color: "#000",
                  "& .MuiMenuItem-root": {
                    "&:hover": {
                      backgroundColor: "#f5f5f5"
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#e0e0e0",
                      "&:hover": {
                        backgroundColor: "#d5d5d5"
                      }
                    }
                  }
                },
              },
            }}
            multiple
            name="BranchesIds"
            value={selectedBranches}
            onChange={handleBranchChange}
            displayEmpty
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <span className="block truncate">
                    {t("managers.branchesIds")}
                  </span>
                );
              }
              return selected
                .map((id) => branches.find((b) => b.branchId === id)?.name)
                .join(", ");
            }}
          >
            <MenuItem
              sx={{
                fontFamily: "Montserrat",
              }}
              disabled
              value=""
            >
              <span>{t("managers.branchesIds")}</span>              
            </MenuItem>
            {branches.map((branch) => (
              <MenuItem
                sx={{
                  fontFamily: "Montserrat",
                }}
                key={branch.branchId}
                value={branch.branchId}
              >
                {branch.name}
              </MenuItem>
            ))}
          </Select>
          {errors.branchesIds && (
            <FormHelperText>{errors.branchesIds}</FormHelperText>
          )}
        </FormControl>

        <div className="w-full flex flex-col gap-4">
          <ButtonForm loading={loading}>
            {t("managers.create.submit")}
          </ButtonForm>
        </div>
      </div>
    </form>
  );
}

